import React, { useEffect, useState } from "react";
import { X, ArrowRight, Copy } from "lucide-react";
import { toast } from "sonner";

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Ya" : "Tidak";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) return new Date(v).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};
const label = (k) => k.replace(/[._]/g, " ").replace(/^\w/, (c) => c.toUpperCase());

// Split audit meta into before→after diffs and plain details.
export function splitMeta(meta) {
  const diffs = [], details = [];
  const m = meta && typeof meta === "object" ? meta : {};
  Object.entries(m.changed || {}).forEach(([k, v]) => diffs.push(Array.isArray(v) && v.length === 2 ? { k, from: v[0], to: v[1] } : { k, from: undefined, to: v, unknown: true }));
  if ("from" in m && "to" in m) diffs.push({ k: "nilai", from: m.from, to: m.to });
  if (m.before && m.after && typeof m.before === "object" && typeof m.after === "object") {
    new Set([...Object.keys(m.before), ...Object.keys(m.after)]).forEach((k) => { if (JSON.stringify(m.before[k]) !== JSON.stringify(m.after[k])) diffs.push({ k, from: m.before[k], to: m.after[k] }); });
  }
  Object.entries(m).forEach(([k, v]) => {
    if (["changed", "before", "after"].includes(k) || (k === "from" || k === "to") && "from" in m && "to" in m) return;
    details.push({ k, v });
  });
  return { diffs, details };
}

function DiffTable({ diffs }) {
  return (
    <div className="mt-2 overflow-hidden rounded-xl border border-line" data-testid="audit-detail-diffs">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><tr><th className="px-3 py-2 text-left">Bidang</th><th className="px-3 py-2 text-left">Sebelum</th><th className="w-4" /><th className="px-3 py-2 text-left">Sesudah</th></tr></thead>
        <tbody className="divide-y divide-slate-100">
          {diffs.map((d, i) => (
            <tr key={`${d.k}-${i}`} data-testid={`audit-diff-${d.k}`}>
              <td className="px-3 py-2 font-semibold text-slate-700">{label(d.k)}</td>
              <td className="break-all px-3 py-2"><span className="rounded bg-rose-50 px-1.5 py-0.5 text-rose-700">{d.unknown ? "tidak dicatat" : fmtVal(d.from)}</span></td>
              <td className="text-slate-300"><ArrowRight size={12} /></td>
              <td className="break-all px-3 py-2"><span className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">{d.unknown && typeof d.to === "string" ? d.to : fmtVal(d.to)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AuditDetail({ entry, def, onClose }) {
  const [raw, setRaw] = useState(false);
  useEffect(() => { const k = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  const { diffs, details } = splitMeta(entry.meta);
  const Icon = def.icon;
  const copy = () => navigator.clipboard?.writeText(JSON.stringify(entry, null, 2)).then(() => toast.success("JSON disalin"), () => toast.error("Gagal menyalin"));
  const info = [["Aktor", entry.actor_email], ["ID aktor", entry.actor_id], ["Target", entry.target], ["Waktu", fmtVal(entry.created_at)], ["ID entri", entry.id]];
  return (
    <div className="fixed inset-0 z-[120]" data-testid="audit-detail">
      <div className="fade-in absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]" onClick={onClose} data-testid="audit-detail-backdrop" />
      <aside className="absolute inset-y-0 right-0 flex w-full max-w-lg flex-col bg-white shadow-2xl" style={{ animation: "slideInRight .25s ease both" }} role="dialog" aria-modal="true">
        <div className="flex items-start gap-3 border-b border-line p-5">
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${def.tint}`}><Icon size={18} /></span>
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-slate-900" data-testid="audit-detail-title">{def.label}</p>
            <p className="mt-0.5 text-xs text-slate-500">{def.describe(entry.target, entry.meta)}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Tutup" data-testid="audit-detail-close"><X size={18} /></button>
        </div>
        <div className="flex-1 space-y-6 overflow-y-auto p-5">
          <dl className="grid grid-cols-[96px_1fr] gap-x-3 gap-y-2 text-xs" data-testid="audit-detail-info">
            {info.map(([k, v]) => <React.Fragment key={k}><dt className="font-semibold text-slate-500">{k}</dt><dd className="break-all text-slate-800">{v || "—"}</dd></React.Fragment>)}
          </dl>
          <section>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Sebelum → Sesudah</p>
            {diffs.length ? <DiffTable diffs={diffs} /> : <p className="mt-2 rounded-xl bg-slate-50 px-3 py-3 text-xs text-slate-500" data-testid="audit-detail-nodiff">Server tidak mencatat nilai sebelum/sesudah untuk aksi ini.</p>}
          </section>
          {details.length > 0 && (
            <section>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Detail tambahan</p>
              <dl className="mt-2 grid grid-cols-[120px_1fr] gap-x-3 gap-y-2 rounded-xl border border-line p-3 text-xs" data-testid="audit-detail-extra">
                {details.map(({ k, v }) => <React.Fragment key={k}><dt className="font-semibold text-slate-500">{label(k)}</dt><dd className="break-all text-slate-800">{fmtVal(v)}</dd></React.Fragment>)}
              </dl>
            </section>
          )}
          <section>
            <div className="flex items-center justify-between">
              <button onClick={() => setRaw((v) => !v)} className="text-xs font-bold text-brand hover:underline" data-testid="audit-detail-raw-toggle">{raw ? "Sembunyikan" : "Lihat"} JSON mentah</button>
              <button onClick={copy} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800" data-testid="audit-detail-copy"><Copy size={12} /> Salin</button>
            </div>
            {raw && <pre className="mt-2 max-h-72 overflow-auto rounded-xl bg-navy p-3 text-[11px] leading-relaxed text-white/80" data-testid="audit-detail-raw">{JSON.stringify(entry, null, 2)}</pre>}
          </section>
        </div>
      </aside>
    </div>
  );
}
